import React, { useState } from "react";
import { Button, CircularProgress, Box } from "@mui/material";
import { useSelector } from "react-redux";
import { Field, FieldArray } from "formik";
import { Moment } from "moment";

import { DefaultExpirationService } from "../utils";
import PlansPriceData from "../../PlansPriceData";
import { useAdminProviderDataContext } from "../../../../../../../context";
import { FormDatePicker } from "../../../../../../../components/ui";
import { DATE_FORMAT } from "../../../../../../../constants";
import { ICustomerPlan } from "../../../../../../../api/accounts/index.types";
import { Plan } from "../../../../../Customer";
import { IDiscountCodeForPlan } from "../../../../../../../api/discounts/index.types";
import AssignDiscountCodeToPlan from "../../../../AssignDiscountCodeToPlan";

const Status = ({ status }: { status: ICustomerPlan["status"] }) => {
  return status === "active" ? (
    <Box component="td" sx={{ textAlign: "center", color: "green" }}>
      ✔
    </Box>
  ) : (
    <Box component="td" sx={{ textAlign: "center", color: "red" }}>
      ✘
    </Box>
  );
};

interface IProps {
  setEditingPlanData: (p: Plan) => void;
  setEditingPlanDiscountCode: (p: IDiscountCodeForPlan | null) => void;
}

const TableBody: React.FC<IProps> = ({ setEditingPlanData, setEditingPlanDiscountCode }) => {
  const {
    data: { plans },
  } = useSelector((state) => state.customer.plans);
  const plansData = useSelector((state) => state.plans);

  const {
    user: { allowed_features },
  } = useAdminProviderDataContext();

  const { banned_account } = useSelector((state) => state.customer.mainData.data);
  const { account_plans_id } = banned_account?.field_values_before_bun || {};
  const [editingPlanId, setEditingPlanId] = useState(0);

  return (
    <tbody>
      <FieldArray
        name="plans"
        render={({ form }) => {
          return plans.map((plan, index) => {
            const isEditing = editingPlanId === plan.id;
            const isPasses = plan.passed;

            const expiresFieldName = `plans.${index}.expires`;
            const statusFiledName = `plans.${index}.status`;

            const status = form.values.plans[index]?.status;
            const isActiveStatus = status === "active";

            const { isHasExpiration, expirationDate } =
              new DefaultExpirationService().getExpirationData(plan);

            const isAllowedToAssignDiscountCode =
              !plan.discount_code?.code &&
              isActiveStatus &&
              editingPlanId &&
              allowed_features.assign_discount_code_to_customer_plan;

            const isAllowedSaveChanges = isEditing && form.dirty && plansData.loading;
            const isAllowedEditPlan = !!account_plans_id || plan.passed || !form.isValid;

            return (
              <Box component="tr" key={plan.id} sx={{ "& td": { minHeight: "60px", height: "45px" } }}>
                <td>{plan.broker}</td>
                <td>{plan.plan && plan.plan.name}</td>
                <td>{plan.start}</td>
                <td>
                  {isEditing ? (
                    <Box sx={{ maxWidth: "145px", minWidth: "140px" }}>
                      <Field
                        variant="standard"
                        name={expiresFieldName}
                        component={FormDatePicker}
                        onChange={(date: Moment | null) =>
                          form.setFieldValue(
                            expiresFieldName,
                            date ? date.format(DATE_FORMAT) : null
                          )
                        }
                      />
                    </Box>
                  ) : (
                    plan.expires
                  )}
                </td>

                <PlansPriceData plan={plan} index={index} isEditing={isEditing} />
                {isEditing ? (
                  <Box component="td" sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "50px" }}>
                    <input
                      disabled={!allowed_features.edit_customer_active_status_subscriptions}
                      style={{ cursor: "pointer" }}
                      type="checkbox"
                      checked={isActiveStatus}
                      onChange={() => {
                        form.setFieldValue(statusFiledName, isActiveStatus ? "canceled" : "active");
                      }}
                    />
                  </Box>
                ) : (
                  <>{isPasses ? <td>Passed</td> : <Status status={plan.status} />}</>
                )}

                <td>
                  {isAllowedToAssignDiscountCode ? (
                    <AssignDiscountCodeToPlan
                      setEditingPlanDiscountCode={setEditingPlanDiscountCode}
                      form={form}
                      index={index}
                      plan={plan}
                    />
                  ) : (
                    plan.discount_code?.code
                  )}
                </td>
                <td>{isHasExpiration ? expirationDate : ""}</td>
                <td>{plan.free_resets}</td>
                <td>
                  <Button
                    size="small"
                    onClick={() => {
                      if (form.dirty && isEditing) {
                        setEditingPlanData(form.values.plans[index]);
                        form.resetForm();
                        setEditingPlanId(0);
                        return;
                      }
                      form.resetForm();
                      isEditing ? setEditingPlanId(0) : setEditingPlanId(plan.id);
                    }}
                    disabled={isAllowedEditPlan}
                    {...(isAllowedSaveChanges && {
                      endIcon: <CircularProgress size={14} />,
                      /* disable save button until plans data will be loaded,
                       * plans data required for the correct calculating of a new price
                       * before show confirm window */
                      disabled: plansData.loading,
                    })}
                  >
                    {isEditing ? (form.dirty ? "Save" : "Cancel") : "Edit"}
                  </Button>
                </td>
              </Box>
            );
          });
        }}
      />
    </tbody>
  );
};

export default TableBody;
